import cn from 'classnames'
import { memo } from 'react'

import { ProgressTrack } from './components/progressTrack/ProgressTrack'
import { Step } from './components/step/Step'
import { IStepperProps, TStepperStepStatus } from './models'

import styles from './Stepper.module.scss'

/**
 * Created by plexideas on 02.05.23
 */
const getTrackStatus = (
  prevStepStatus: TStepperStepStatus | undefined,
  nextStepStatus: TStepperStepStatus
): TStepperStepStatus => {
  if (nextStepStatus === 'current') {
    return 'current'
  }

  const isComplete = prevStepStatus === 'complete' || nextStepStatus === 'complete'

  if (isComplete) {
    return 'complete'
  }

  if (nextStepStatus === 'disabled') {
    return 'disabled'
  }

  return 'incomplete'
}

export const Stepper = memo<IStepperProps>(
  ({
    steps,
    direction = 'horizontal',
    variant = 'standard',
    onClick,
    color = 'primary',
    size = 36,
    highlightCompletedStep = false,
    withTrackGap = true,
    fullWidth = false,
  }) => {
    const isVertical = direction === 'vertical'
    const isHorizontalFullWidth = fullWidth && !isVertical
    const isIndicator = variant === 'indicator'
    const resolvedStepStatuses = steps.map(({ status }) => status)

    return (
      <div
        className={cn(styles.container, {
          [styles.vertical]: isVertical,
          [styles.fullWidth]: isHorizontalFullWidth,
          [styles.variantIndicator]: isIndicator,
        })}
      >
        {steps.map((step, index) => {
          const stepStatus = resolvedStepStatuses[index]
          const isNotFirstStep = index > 0
          const isNotLastStep = index < steps.length - 1
          const stepKey = `step-${index}`
          const hasTrack = variant !== 'indicator'
          const previousStepStatus = resolvedStepStatuses[index - 1]
          const nextStepStatus = resolvedStepStatuses[index + 1]
          const trackStatusBefore = getTrackStatus(previousStepStatus, stepStatus)
          const trackStatusAfter =
            nextStepStatus !== undefined ? getTrackStatus(stepStatus, nextStepStatus) : undefined
          const trackAfterStep =
            hasTrack && isNotLastStep && isVertical && trackStatusAfter ? (
              <ProgressTrack
                status={trackStatusAfter}
                direction={direction}
                variant={variant}
                color={color}
                size={size}
                highlightCompletedStep={highlightCompletedStep}
                withTrackGap={withTrackGap}
                fullWidth={isHorizontalFullWidth}
                inlineVertical
                fromCurrentStep={stepStatus === 'current'}
              />
            ) : null

          return (
            <div
              className={cn(styles.item, {
                [styles.vertical]: isVertical,
                [styles.fullWidthItem]: isHorizontalFullWidth && isNotFirstStep,
              })}
              key={stepKey}
            >
              {hasTrack && isNotFirstStep && !isVertical && (
                <ProgressTrack
                  status={trackStatusBefore}
                  direction={direction}
                  variant={variant}
                  color={color}
                  size={size}
                  highlightCompletedStep={highlightCompletedStep}
                  withTrackGap={withTrackGap}
                  fullWidth={isHorizontalFullWidth}
                  fromCurrentStep={previousStepStatus === 'current'}
                />
              )}
              <Step
                stepIndex={index}
                direction={direction}
                variant={variant}
                onClick={onClick}
                color={color}
                size={size}
                highlightCompletedStep={highlightCompletedStep}
                trackAfter={trackAfterStep}
                {...step}
                status={stepStatus}
              />
            </div>
          )
        })}
      </div>
    )
  }
)

Stepper.displayName = 'Stepper'
